import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Form } from 'react-bootstrap';
import { formatDateTime } from '../utils/formatDate';

const EVENT_LABEL = {
  'sistema.ping': 'Prueba de conexión',
  'cliente.renombrado': 'Cliente renombrado',
  'pedido.vacios_recibidos': 'Vacíos recibidos',
  'pedido.llenado': 'Tanques llenados',
  'pedido.despachado': 'Despacho',
  'pedido.entrega_confirmada': 'Entrega confirmada',
  'pedido.despacho_modificado': 'Despacho modificado',
  'pedido.despacho_eliminado': 'Despacho eliminado',
  'pedido.devolucion_recibida': 'Devolución recibida',
  'pedido.devolucion_solicitada': 'Devolución solicitada',
  'pedido.devolucion_cancelada': 'Devolución cancelada',
  'pedido.cancelado': 'Pedido cancelado',
};
const label = (type) => EVENT_LABEL[type] || type;

const post = async (url, body) => {
  const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || 'La acción falló.');
  return data;
};

// Admin dashboard → "Sincronización con Ventas": the link with Ventas (pedidos, CG Connect). Everything Ventas must
// know is saved and retried automatically (backend utils/integrationOutbox.js); this shows what's still waiting or was
// refused, recent despachos whose record in Ventas disagrees (utils/ventasReconcile.js — "Corregir" re-sends what
// this side knows), and empties still in the plant for a pedido Ventas cancelled.
const VentasSyncPanel = () => {
  const [status, setStatus] = useState(null);
  const [recon, setRecon] = useState(null);
  const [cancelled, setCancelled] = useState([]);
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState(null); // { type, text }

  const loadStatus = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([fetch('/api/ventas/sync-status').then((r) => r.json()), fetch('/api/ventas/cancelled-arrivals').then((r) => r.json())]);
      setStatus(s);
      setCancelled(Array.isArray(c) ? c : []);
    } catch (err) {
      console.error('❌ Error loading Ventas sync status:', err);
    }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const run = async (key, fn, ok) => {
    setBusy(key);
    setMessage(null);
    try {
      const result = await fn();
      if (ok) setMessage({ type: 'success', text: typeof ok === 'function' ? ok(result) : ok });
      await loadStatus();
      return result;
    } catch (err) {
      setMessage({ type: 'danger', text: err.message });
      return null;
    } finally {
      setBusy('');
    }
  };

  const check = () => run('recon', async () => {
    const r = await fetch(`/api/ventas/reconcile?days=${days}`).then((res) => res.json());
    setRecon(r);
    if (r.reachable === false) throw new Error(`Ventas no respondió: ${r.message || ''}`);
    return r;
  }, (r) => (r.issues.length ? `${r.issues.length} diferencias en ${r.checked} despachos.` : `Todo coincide (${r.checked} despachos revisados).`));

  const fixOne = (i) => run(`${i.dispatchId}-${i.issue}`, () => post(`/api/ventas/reconcile/${i.dispatchId}/fix`, { issue: i.issue }),
    (r) => (r.fixed ? `Corregido: despacho "${i.noteNumber}".` : `No se corrigió: ${r.message || (r.remaining || []).join(', ')}`))
    .then((r) => { if (r?.fixed) setRecon((prev) => ({ ...prev, issues: prev.issues.filter((x) => !(x.dispatchId === i.dispatchId && x.issue === i.issue)) })); });

  const counts = status?.outbox?.counts || {};
  const failed = (status?.outbox?.problems || []).filter((p) => p.status === 'failed');
  const retrying = (status?.outbox?.problems || []).filter((p) => p.status !== 'failed');
  const rejectedHere = status?.inbox?.rejected || [];

  return (
    <div className="row mb-4">
      <div className="col-12">
        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center flex-wrap mb-2" style={{ gap: '8px' }}>
              <Card.Title className="mb-0">Sincronización con Ventas</Card.Title>
              <div className="d-flex" style={{ gap: '8px' }}>
                <Button size="sm" variant="outline-secondary" disabled={busy === 'ping'}
                  onClick={() => run('ping', () => post('/api/ventas/sync-ping'), (r) => (r.status === 'sent' ? 'Ventas respondió: la conexión funciona.' : `Ventas no respondió (${r.status}).`))}>
                  <i className="bi bi-broadcast"></i> Probar conexión
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={loadStatus}><i className="bi bi-arrow-clockwise"></i></Button>
              </div>
            </div>
            <div className="small text-muted mb-3">
              Enviados: {counts.sent || 0} · En cola: {(counts.pending || 0) + (counts.sending || 0)} · Rechazados por Ventas: {counts.failed || 0} · Rechazados aquí: {rejectedHere.length}
            </div>
            {message && <div className={`alert alert-${message.type} py-2`}>{message.text}</div>}

            {cancelled.length > 0 && (
              <>
                <h6 className="text-mm-yellow">Pedidos cancelados con vacíos en planta</h6>
                <Table size="sm" className="mb-3">
                  <tbody>
                    {cancelled.map((e) => (
                      <tr key={e._id}>
                        <td>{e.quantity} vacíos de {e.client}</td>
                        <td className="small">Pedido {e.ventasPedidoCancelado.pedidoNumber}{e.ventasPedidoCancelado.reason ? ` — ${e.ventasPedidoCancelado.reason}` : ''}</td>
                        <td className="small text-muted">{formatDateTime(e.ventasPedidoCancelado.at)}</td>
                        <td className="text-right">
                          <Button size="sm" variant="outline-secondary" disabled={busy === e._id}
                            onClick={() => run(e._id, () => post(`/api/ventas/cancelled-arrivals/${e._id}/ack`), 'Aviso atendido.')}>Atendido</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}

            {failed.length > 0 && (
              <>
                <h6>Rechazados por Ventas <span className="small text-muted">(no se reintentan solos)</span></h6>
                <Table size="sm" className="mb-3">
                  <tbody>
                    {failed.map((p) => (
                      <tr key={p.eventId}>
                        <td>{label(p.type)}<div className="small text-muted">{p.data?.noteNumber || p.data?.pedidoId || ''}</div></td>
                        <td className="small text-muted">{formatDateTime(p.createdAt)}</td>
                        <td className="small">{p.lastHttpStatus ? `${p.lastHttpStatus} · ` : ''}{p.lastError}</td>
                        <td className="text-right">
                          <Button size="sm" variant="outline-secondary" disabled={busy === p.eventId}
                            onClick={() => run(p.eventId, () => post(`/api/ventas/sync-retry/${p.eventId}`), 'Evento en cola de nuevo.')}>Reintentar</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}

            {retrying.length > 0 && (
              <>
                <h6>Reintentando <span className="small text-muted">(Ventas no respondió; se reintenta solo)</span></h6>
                <Table size="sm" className="mb-3">
                  <tbody>
                    {retrying.map((p) => (
                      <tr key={p.eventId}>
                        <td>{label(p.type)}</td>
                        <td className="small text-muted">{formatDateTime(p.createdAt)} · {p.attempts} intentos</td>
                        <td className="small">{p.lastError}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}

            <div className="d-flex align-items-end flex-wrap mb-2" style={{ gap: '8px' }}>
              <div>
                <h6 className="mb-1">Comparar despachos con Ventas</h6>
                <div className="small text-muted">Despachos vinculados a pedidos cuyo registro en Ventas no coincide con el de aquí.</div>
              </div>
              <Form.Select size="sm" style={{ width: 'auto' }} value={days} onChange={(e) => setDays(Number(e.target.value))}>
                <option value={7}>Últimos 7 días</option>
                <option value={30}>Últimos 30 días</option>
                <option value={90}>Últimos 90 días</option>
              </Form.Select>
              <Button size="sm" disabled={busy === 'recon'} onClick={check}>{busy === 'recon' ? 'Comparando…' : 'Comparar'}</Button>
            </div>
            {recon?.issues?.length > 0 && (
              <Table size="sm" className="mb-0">
                <thead><tr><th>Despacho</th><th>Pedido</th><th>Diferencia</th><th /></tr></thead>
                <tbody>
                  {recon.issues.map((i) => (
                    <tr key={`${i.dispatchId}-${i.issue}`}>
                      <td>{i.noteNumber}<div className="small text-muted">{i.quantity} cilindros · {formatDateTime(i.date)}</div></td>
                      <td className="small">{i.pedidoNumber || '—'}</td>
                      <td className="small">{i.text}{i.detail ? <div className="text-muted">{i.detail}</div> : null}</td>
                      <td className="text-right">
                        {i.fixable
                          ? <Button size="sm" variant="outline-primary" disabled={busy === `${i.dispatchId}-${i.issue}`} onClick={() => fixOne(i)}>Corregir</Button>
                          : <span className="small text-muted">Revisar a mano</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default VentasSyncPanel;
